import React, { useRef, useState } from 'react';
import { TimeEntryType } from '../../interfaces/TimesheetInterface';
import {
  PayrollEntryResult, PayrollHourlyEntryBody,
} from '../../interfaces/TimesheetEditInterface';
import {
  formatClockMinutes, instantToLocalWallTime, parseClock, PUNCH_CROSSES_MIDNIGHT_CODE,
  PUNCH_CROSSES_MIDNIGHT_MESSAGE, resolveOutMinutes, wallTimeToTenantInstant, ymdLabel,
} from './clockParse';
import ProjectPicker, { PickedProject } from './ProjectPicker';
import { EntryErrorBanner, EntryModalShell, performEntrySave, PunchErrorSlot } from './EntryModalShell';
import { PunchEchoParts } from './echoParts';
import { HourlyEntryTarget } from './HourlyEntryModal';

/**
 * Piece hours punch editor (2020PH/NV — clockHourlyV2Config 'HourlyJob'). The
 * time a piece worker spends on a job, stored as a TimeEntryHourly row with a
 * project and NO phase / account / service order; saves through the `hourly`
 * route. Same shell as the piece punch modal (Type + In/Out 10-key fields,
 * unmoved punch ends sent verbatim so a no-op save never relabels a device
 * punch) plus a required job.
 */

export interface PieceHoursCarry {
  project: PickedProject | null;
  type: number;
}

export interface PieceHoursModalProps {
  employeeID: number;
  payPeriodID: number;
  ymd: string;
  employeeName: string;
  editSessionID: string;
  entry: HourlyEntryTarget | null;
  carry: PieceHoursCarry | null;
  onSaved: (result: PayrollEntryResult, close: boolean, carry: PieceHoursCarry) => void;
  onCancel: () => void;
  onConflict: (message: string) => void;
}

const PieceHoursModal: React.FC<PieceHoursModalProps> = ({
  employeeID, payPeriodID, ymd, employeeName, editSessionID,
  entry, carry, onSaved, onCancel, onConflict,
}) => {
  const isEdit = entry != null;
  const row = entry?.row ?? null;
  const origIn = row ? instantToLocalWallTime(row.inDateTime, row.inDateTimeOffset) : null;
  const origOut = row && row.outDateTime
    ? instantToLocalWallTime(row.outDateTime, row.outDateTimeOffset)
    : null;
  const baseYmd = origIn?.ymd ?? ymd;
  const origOutMinutes = origOut != null && origIn != null
    ? (origOut.ymd > origIn.ymd ? origOut.minutes + 1440 : origOut.minutes)
    : null;

  const initialProject: PickedProject | null = isEdit
    ? (entry!.group.project?.projectID == null
      ? null
      : {
        projectID: entry!.group.project.projectID,
        altProjectID: entry!.group.project.altProjectID ?? '',
        name: entry!.group.project.name ?? '',
      })
    : carry?.project ?? null;

  const [project, setProject] = useState<PickedProject | null>(initialProject);
  const [type, setType] = useState<number>(row ? row.type : carry?.type ?? TimeEntryType.Regular);
  const [inRaw, setInRaw] = useState<string>(origIn ? formatClockMinutes(origIn.minutes) : '');
  const [outRaw, setOutRaw] = useState<string>(origOutMinutes != null ? formatClockMinutes(origOutMinutes) : '');
  const [bad, setBad] = useState<'in' | 'out' | 'project' | null>(null);
  const [banner, setBanner] = useState<{ title: string; body?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  /** Cross-midnight refusal in the fixed slot under In / Out (client check or the API's 400). */
  const [punchErr, setPunchErr] = useState<string | null>(null);
  const inRef = useRef<HTMLInputElement>(null);
  const outRef = useRef<HTMLInputElement>(null);
  /** No job yet (a fresh add) → the picker takes first focus; otherwise In does. */
  const pickerFirst = initialProject == null;

  const handleSave = async (close: boolean) => {
    const i = parseClock(inRaw);
    if (i == null) { setBad('in'); inRef.current?.focus(); return; }
    let outMinutes: number | null = null;
    if (outRaw.trim() !== '') {
      const o = parseClock(outRaw);
      if (o == null) { setBad('out'); outRef.current?.focus(); return; }
      // Earlier than the in → next local day; only up to 12:00 AM (sent as next-day 00:00).
      const out = resolveOutMinutes(i, o);
      if (out.crossesMidnight) {
        setPunchErr(PUNCH_CROSSES_MIDNIGHT_MESSAGE);
        setBad('out');
        outRef.current?.focus();
        return;
      }
      outMinutes = out.minutes;
    }
    if (!project) {
      setBad('project');
      setBanner({ title: 'Pick a job' });
      return;
    }

    const inUnchanged = isEdit && origIn != null && i === origIn.minutes;
    const outUnchanged = isEdit && outMinutes != null && origOutMinutes != null && outMinutes === origOutMinutes;

    const inStamp = inUnchanged
      ? { iso: row!.inDateTime, offset: row!.inDateTimeOffset }
      : wallTimeToTenantInstant(baseYmd, i);
    const outStamp = outMinutes == null
      ? null
      : outUnchanged
        ? { iso: row!.outDateTime!, offset: row!.outDateTimeOffset! }
        : wallTimeToTenantInstant(baseYmd, outMinutes);

    const body: PayrollHourlyEntryBody = {
      ...(isEdit ? {} : { employeeID, payPeriodID }),
      inDateTime: inStamp.iso,
      inDateTimeOffset: inStamp.offset,
      outDateTime: outStamp?.iso ?? null,
      outDateTimeOffset: outStamp?.offset ?? null,
      type,
      // Piece hours = project only; 0 is the contract's "none" sentinel (§8.1).
      projectID: project.projectID,
      accountCodeID: 0,
      projectPhaseID: 0,
      customerServiceWarrantyID: 0,
      serviceOrderText: '',
      serviceOrderID: null,
      serviceOrderScopeID: null,
      tempServiceOrderDescription: null,
      locationName: entry?.group.locationName ?? '',
      description: entry?.group.description ?? '',
      editSessionID,
    };

    setSaving(true);
    setBanner(null);
    setPunchErr(null);
    const outcome = await performEntrySave('hourly', isEdit ? row!.timeEntryHourlyID : null, body);
    setSaving(false);
    if (!outcome.ok) {
      if (outcome.conflict) onConflict(outcome.message);
      else if (outcome.code === PUNCH_CROSSES_MIDNIGHT_CODE) { setPunchErr(outcome.message); setBad('out'); }
      else setBanner({ title: 'Could not save the piece hours punch.', body: outcome.message });
      return;
    }
    onSaved(outcome.result, close, { project, type });
  };

  return (
    <EntryModalShell
      kind="pieceHours"
      mode={isEdit ? 'edit' : 'add'}
      subtitle={`${ymdLabel(baseYmd)} · ${employeeName}`}
      banner={banner ? <EntryErrorBanner title={banner.title} body={banner.body} /> : null}
      echo={<PunchEchoParts inRaw={inRaw} outRaw={outRaw} />}
      saving={saving}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <p className="pe-note">
        Piece hours — time on the job, no phase or cost code. Payroll-created punches carry no GPS and no selfie.
      </p>
      <div className="row2">
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <label>Job</label>
          <ProjectPicker
            value={project}
            onChange={(p) => {
              setProject(p);
              if (bad === 'project') { setBad(null); setBanner(null); }
              if (p) window.setTimeout(() => inRef.current?.focus(), 0);
            }}
            autoFocus={pickerFirst}
            bad={bad === 'project'}
          />
        </div>
      </div>
      <div className="row2">
        <div className="field">
          <label>Type</label>
          <select className="pe-in sans" value={type} onChange={(ev) => setType(Number(ev.target.value))}>
            <option value={TimeEntryType.Regular}>Regular</option>
            <option value={TimeEntryType.Meal}>Meal</option>
          </select>
        </div>
      </div>
      <div className="row2">
        <div className="field">
          <label>In</label>
          <input
            ref={inRef}
            className={`pe-in${bad === 'in' ? ' bad' : ''}`}
            value={inRaw}
            onChange={(ev) => { setInRaw(ev.target.value); setPunchErr(null); if (bad === 'in') setBad(null); }}
            placeholder="730"
            autoFocus={!pickerFirst}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="field">
          <label>Out</label>
          <input
            ref={outRef}
            className={`pe-in${bad === 'out' ? ' bad' : ''}`}
            value={outRaw}
            onChange={(ev) => { setOutRaw(ev.target.value); setPunchErr(null); if (bad === 'out') setBad(null); }}
            placeholder="1500"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
      <PunchErrorSlot message={punchErr} />
    </EntryModalShell>
  );
};

export default PieceHoursModal;
